/**
 * Finance › Refund approvals (W6-REFUND-FOUR-EYES, D398, ADR-0063) — framework-neutral view-models for the queue of
 * refunds and credits above the governed threshold: statuses and tones, which rows this operator may decide and why
 * not, per-currency totals (never summed across currencies), and the words for each refusal.
 *
 * The console only FILTERS: Approve is hidden on one's own item (with the reason shown), but the authority is the
 * server, which compares the SESSION identities (`403 SELF_APPROVAL`) and caps the approval by the checker's grant in
 * the item's currency. A failed read is shown as an error — never as an empty queue or a zero total.
 */
import { type RefundApprovalView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

/** The statuses the queue can be read by, in the order the screen offers them. */
export const REFUND_QUEUE_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'WITHDRAWN', 'EXECUTED'] as const;
export type RefundQueueStatus = (typeof REFUND_QUEUE_STATUSES)[number];

export type RefundTone = 'neutral' | 'success' | 'warning' | 'danger';

export function refundStatusTone(status: string): RefundTone {
  return status === 'PENDING' ? 'warning' : status === 'APPROVED' || status === 'EXECUTED' ? 'success' : status === 'REJECTED' || status === 'WITHDRAWN' ? 'danger' : 'neutral';
}

/** The kinds the screen names in words (anything else is shown as its code). */
export const REFUND_KINDS = ['FOOD_REFUND', 'FOOD_CHARGEBACK', 'SUPPORT_REFUND', 'SUPPORT_COUPON', 'PARTNER_CREDIT', 'DISPUTE_CREDIT'] as const;
/** Why a refund went where it went (the server's routing reason codes). */
export const REFUND_ROUTE_REASONS = ['WITHIN_REFUND_THRESHOLD', 'ABOVE_REFUND_THRESHOLD', 'ABOVE_MAKER_LIMIT', 'REFUND_THRESHOLD_UNSET', 'REFUND_THRESHOLD_INVALID', 'REFUND_THRESHOLD_CURRENCY_MISMATCH'] as const;

/** Why Approve is NOT offered to this operator on this item, or undefined when it is. Display only. */
export type RefundApprovalBlock = 'NOT_PENDING' | 'OWN_REQUEST' | 'NO_PERMISSION';

const sameId = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

export function refundApprovalBlock(row: Pick<RefundApprovalView, 'status' | 'requestedBy'>, operatorId: string | undefined, mayDecide: boolean, selfApproval = false): RefundApprovalBlock | undefined {
  if (row.status !== 'PENDING') return 'NOT_PENDING';
  if (!selfApproval && operatorId !== undefined && sameId(row.requestedBy, operatorId)) return 'OWN_REQUEST';
  if (!mayDecide) return 'NO_PERMISSION';
  return undefined;
}

/** Whether this operator may reject (or, when it is the maker, withdraw) a pending item. */
export function refundRejectable(row: Pick<RefundApprovalView, 'status' | 'requestedBy'>, operatorId: string | undefined, mayDecide: boolean): boolean {
  if (row.status !== 'PENDING') return false;
  return mayDecide || (operatorId !== undefined && sameId(row.requestedBy, operatorId));
}

/** The operator's authority on the queue, from its grants (unknown grants → the server decides). */
export function refundAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayDecide: boolean; readonly mayPropose: boolean; readonly mayCredit: boolean } {
  if (grants === undefined) return { mayView: true, mayDecide: true, mayPropose: true, mayCredit: true };
  return {
    mayView: grantsAllow(grants, { permission: 'refund:view', target: 'PLATFORM' }),
    mayDecide: grantsAllow(grants, { permission: 'refund:approve', target: 'PLATFORM' }),
    // A compensation proposal is Finance's own money word at the ticket's market (GEO).
    mayPropose: grantsAllow(grants, { permission: 'payment:refund', target: 'GEO' }),
    // A partner credit note is a PLATFORM word (`POST /v1/admin/partner-statements/:id/credit-notes`).
    mayCredit: grantsAllow(grants, { permission: 'partner_billing:credit', target: 'PLATFORM' }),
  };
}

/** Exact per-currency totals of a list (bigint; never summed across currencies). */
export function summarizeRefundQueue(rows: readonly Pick<RefundApprovalView, 'amount'>[]): readonly { readonly currency: string; readonly totalMinor: string; readonly count: number }[] {
  const by = new Map<string, { total: bigint; count: number }>();
  for (const r of rows) {
    if (!/^\d+$/.test(r.amount.amountMinor)) continue;
    const cur = by.get(r.amount.currency) ?? { total: 0n, count: 0 };
    by.set(r.amount.currency, { total: cur.total + BigInt(r.amount.amountMinor), count: cur.count + 1 });
  }
  return [...by.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, v]) => ({ currency, totalMinor: v.total.toString(), count: v.count }));
}

/** The subject of an item in words the screen can render: its kind (`food` / `support` / a partner `statement`) and its opaque reference. */
export function refundSubject(subjectRef: string): { readonly kind: 'food' | 'support' | 'statement' | 'other'; readonly ref: string } {
  const i = subjectRef.indexOf(':');
  const kind = i > 0 ? subjectRef.slice(0, i) : '';
  const ref = i > 0 ? subjectRef.slice(i + 1) : subjectRef;
  return { kind: kind === 'food' || kind === 'support' || kind === 'statement' ? kind : 'other', ref };
}

